import React, { useState, useMemo, useRef, useEffect } from "react";
import MareyChart from "./chart";

const routesList = require("../files/lists.json").st;

// ─── Theme tokens (from design) ──────────────────────────────────────────────
const LIGHT = {
  name:      "light",
  bg:        "#f3f1ec",
  surface:   "#ffffff",
  surface2:  "#faf8f3",
  surface3:  "#ede9df",
  border:    "rgba(36,32,24,0.12)",
  borderS:   "rgba(36,32,24,0.06)",
  text:      "#1c1a16",
  textSec:   "#5a5448",
  textMuted: "#8a8273",
  primary:   "#3f6c8f",
  accent:    "#5b8a72",
  signal:    "#c87a6a",
  warn:      "#c89b3c",
  shadow:    "rgba(36,32,24,0.10)",
  plotBg:    "#fbfaf5",
  grid:      "rgba(36,32,24,0.18)",
  gridSoft:  "rgba(36,32,24,0.08)",
};

const DARK = {
  name:      "dark",
  bg:        "#0a0e13",
  surface:   "#141a22",
  surface2:  "#1c232d",
  surface3:  "#2a3441",
  border:    "rgba(255,255,255,0.14)",
  borderS:   "rgba(255,255,255,0.07)",
  text:      "#f1ede4",
  textSec:   "#c2bcae",
  textMuted: "#8a8270",
  primary:   "#8bb8d4",
  accent:    "#9bc4a9",
  signal:    "#ec9c87",
  warn:      "#e8c479",
  shadow:    "rgba(0,0,0,0.6)",
  plotBg:    "#0f141b",
  grid:      "rgba(255,255,255,0.22)",
  gridSoft:  "rgba(255,255,255,0.09)",
};

// ─── Constants ────────────────────────────────────────────────────────────────
const BGD_MS  = 3 * 3600 * 1000;
const DAYS_AR       = ["الأحد","الاثنين","الثلاثاء","الأربعاء","الخميس","الجمعة","السبت"];
const DAYS_SHORT_AR = ["أحد","اثن","ثلا","أرب","خمس","جمع","سبت"];
const MONTHS_AR     = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
// Muted palette for light backgrounds
const BRAND_COLORS_LIGHT = [
  "#5b8a72","#c87a6a","#3f6c8f","#c89b3c","#8a5a7a",
  "#6b8e8e","#a0673a","#4a6f8c","#7a8a4f","#b56b78",
  "#5e7a91","#a17c5b",
];

// Bright palette for dark backgrounds — each color is the light version lifted in L* and S
const BRAND_COLORS_DARK = [
  "#7ecda8", // sage → mint
  "#f0a090", // salmon → coral
  "#70b8e8", // steel → sky blue
  "#f0cc70", // amber → gold
  "#d488c4", // berry → orchid
  "#7ed4d4", // teal → cyan
  "#e09060", // rust → tangerine
  "#6ab8e0", // slate → periwinkle
  "#c0d464", // olive → lime
  "#f090a8", // rose → hot pink
  "#88c0e8", // dusk → cornflower
  "#e0b880", // sand → peach
];

function hashColor(name, dark) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  const palette = dark ? BRAND_COLORS_DARK : BRAND_COLORS_LIGHT;
  return palette[Math.abs(h) % palette.length];
}

// In dark theme, lift any color whose perceived brightness is too low to be readable
function ensureReadable(hex, dark) {
  if (!dark || !hex || hex.length < 7) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255; // perceived brightness 0–1
  if (lum >= 0.38) return hex; // already bright enough
  const max = Math.max(r, g, b);
  if (max === 0) return "#88aacc"; // pure black → default blue-grey
  const factor = Math.min(255 / max, 3.8);
  const nr = Math.min(255, Math.round(r * factor));
  const ng = Math.min(255, Math.round(g * factor));
  const nb = Math.min(255, Math.round(b * factor));
  return "#" + nr.toString(16).padStart(2, "0") +
               ng.toString(16).padStart(2, "0") +
               nb.toString(16).padStart(2, "0");
}

function fmtDateAr(date) {
  return `${DAYS_AR[date.getDay()]} ${date.getFullYear()}/${String(date.getMonth()+1).padStart(2,"0")}/${String(date.getDate()).padStart(2,"0")}`;
}

function pad(n) { return String(n).padStart(2, "0"); }

// ─── Icons (line SVG, currentColor) ──────────────────────────────────────────
const I = {
  menu:    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>,
  sun:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>,
  moon:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"/></svg>,
  route:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="2"/><circle cx="18" cy="5" r="2"/><path d="M6 17V9a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v0"/></svg>,
  cal:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>,
  train:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="16" rx="2"/><path d="M4 11h16M8 15h.01M16 15h.01M8 19l-2 3M16 19l2 3"/></svg>,
  chev:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  arrowUp: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>,
  arrowDn: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>,
  exec:    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  pin:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  expand:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>,
  shrink:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>,
  download: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
};

// ─── Brand mark (concentric rail-track circles + signal bar) ─────────────────
function BrandMark({ size = 28, sage, salmon }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke={sage} strokeWidth="2.5" strokeDasharray="2 4" />
      <circle cx="32" cy="32" r="20" stroke={sage} strokeWidth="2.5" />
      <rect x="6" y="29.5" width="52" height="5" fill={salmon} />
      <circle cx="32" cy="32" r="2.4" fill={sage} />
    </svg>
  );
}

// ─── Live Baghdad clock ───────────────────────────────────────────────────────
function BaghdadClock({ T }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const baghdadMs = now.getTime() + (now.getTimezoneOffset() + 180) * 60000;
  const b  = new Date(baghdadMs);
  const hh = pad(b.getHours()), mm = pad(b.getMinutes()), ss = pad(b.getSeconds());
  const day = DAYS_AR[b.getDay()];
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 10,
      background: T.surface2, border: `1px solid ${T.border}`,
      borderRadius: 8, padding: "6px 14px 6px 16px", color: T.text,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: 4, background: T.accent,
        animation: "irr-pulse 2s infinite", flexShrink: 0,
      }} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.05 }}>
        <span style={{
          fontVariantNumeric: "tabular-nums", fontFamily: "JetBrains Mono, monospace",
          fontWeight: 700, fontSize: 20, color: T.text, letterSpacing: ".02em",
        }}>{hh}:{mm}<span style={{ color: T.textSec, fontWeight: 500 }}>:{ss}</span></span>
        <span style={{ fontSize: 10, color: T.textSec, fontFamily: "Cairo, sans-serif", marginTop: 2 }}>
          توقيت بغداد · {day}
        </span>
      </div>
    </div>
  );
}

// ─── Chip ─────────────────────────────────────────────────────────────────────
function Chip({ T, color, children, mono }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: color ? color + "12" : T.surface2,
      border: `1px solid ${color ? color + "33" : T.border}`,
      borderRadius: 4, padding: "3px 8px",
      fontSize: 11, color: color || T.textSec,
      fontFamily: mono ? "JetBrains Mono, monospace" : "Cairo, sans-serif",
      lineHeight: 1.2,
    }}>{children}</span>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────
function Legend({ T, trains, highlight, setHighlight }) {
  return (
    <div style={{
      display: "flex", gap: 6, flexWrap: "wrap", direction: "rtl",
      padding: "8px 14px", background: T.surface2,
      borderTop: `1px solid ${T.borderS}`,
    }}>
      <div style={{
        fontSize: 10.5, color: T.textMuted, letterSpacing: ".08em",
        fontWeight: 700, alignSelf: "center", marginInlineEnd: 6,
      }}>القطارات</div>
      {trains.map((tr, i) => {
        const active = highlight === i;
        return (
          <button key={i}
            onMouseEnter={() => setHighlight(i)}
            onMouseLeave={() => setHighlight(null)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: active ? tr.color + "22" : T.surface,
              border: `1px solid ${active ? tr.color : T.border}`,
              borderRadius: 4, padding: "3px 8px",
              fontSize: 11, color: T.text,
              fontFamily: "JetBrains Mono, monospace",
              cursor: "pointer", transition: "all .12s",
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: 2, background: tr.color, flexShrink: 0 }} />
            {tr.name}
          </button>
        );
      })}
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function Section({ T, label, icon, accent, children }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "visible" }}>
      <div style={{
        padding: "8px 10px 8px 12px", display: "flex", alignItems: "center", gap: 8,
        direction: "rtl", borderBottom: `1px solid ${T.borderS}`, background: T.surface2,
        borderRadius: "8px 8px 0 0",
      }}>
        <span style={{
          width: 22, height: 22, borderRadius: 5,
          background: accent + "22", color: accent,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>{icon}</span>
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".09em", color: T.textSec, textTransform: "uppercase" }}>
          {label}
        </span>
      </div>
      <div style={{ padding: 10 }}>{children}</div>
    </div>
  );
}

function FieldLabel({ T, children }) {
  return (
    <div style={{ fontSize: 10.5, fontWeight: 700, color: T.textMuted, direction: "rtl", marginBottom: 4, letterSpacing: ".04em" }}>
      {children}
    </div>
  );
}

// ─── Custom select (RTL dropdown) ────────────────────────────────────────────
function Select({ T, label, value, onChange, options, getLabel, accent }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);
  const lbl = getLabel || (o => (o && o.caption) || (o && o.$$user_name) || String(o) || "");
  return (
    <div style={{ marginBottom: 8, position: "relative" }} ref={ref}>
      <FieldLabel T={T}>{label}</FieldLabel>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", height: 36, background: T.surface2,
          border: `1px solid ${open ? accent : T.border}`, borderRadius: 6,
          padding: "0 10px", fontFamily: "Cairo, sans-serif", fontSize: 13, color: T.text,
          direction: "rtl", textAlign: "right",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
          cursor: "pointer", transition: "border-color .15s", outline: "none",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
          {lbl(value)}
        </span>
        <span style={{ color: T.textMuted, display: "inline-flex", flexShrink: 0 }}>{I.chev}</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", right: 0, left: 0,
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 6, boxShadow: `0 8px 24px ${T.shadow}`,
          zIndex: 50, maxHeight: 240, overflowY: "auto", direction: "rtl",
        }}>
          {(options || []).map((o, i) => {
            const selected = lbl(o) === lbl(value);
            return (
              <div key={i}
                onClick={() => { onChange(o); setOpen(false); }}
                style={{
                  padding: "7px 12px", fontSize: 12.5, color: T.text, cursor: "pointer",
                  background: selected ? accent + "18" : "transparent",
                  borderRight: selected ? `2px solid ${accent}` : "2px solid transparent",
                  transition: "background .1s",
                }}
                onMouseEnter={e => { if (!selected) e.currentTarget.style.background = T.surface3; }}
                onMouseLeave={e => { if (!selected) e.currentTarget.style.background = selected ? accent + "18" : "transparent"; }}
              >{lbl(o)}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Date field (full month calendar with prev/next navigation) ──────────────
function DateField({ T, value, onChange, accent }) {
  const [open,       setOpen]      = useState(false);
  const [viewYear,   setViewYear]  = useState(value.getFullYear());
  const [viewMonth,  setViewMonth] = useState(value.getMonth());
  const ref = useRef(null);

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  const today = new Date();
  const same  = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth()    === b.getMonth()    &&
    a.getDate()     === b.getDate();

  const firstDow    = new Date(viewYear, viewMonth, 1).getDay(); // 0 = Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null)];
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  const NavBtn = ({ onClick, ch }) => (
    <button onClick={onClick} style={{
      width: 26, height: 26, border: "none", borderRadius: 5,
      background: "transparent", color: T.textSec,
      cursor: "pointer", fontSize: 17, lineHeight: 1,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      transition: "background .1s",
    }}
    onMouseEnter={e => { e.currentTarget.style.background = T.surface3; }}
    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >{ch}</button>
  );

  return (
    <div style={{ marginBottom: 8, position: "relative" }} ref={ref}>
      <FieldLabel T={T}>اليوم</FieldLabel>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", height: 34, background: T.surface2,
          border: `1px solid ${open ? accent : T.border}`, borderRadius: 6,
          padding: "0 10px", fontFamily: "Cairo, sans-serif", fontSize: 12.5, color: T.text,
          direction: "rtl", textAlign: "right",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer", outline: "none",
        }}
      >
        <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}>
          {value.getFullYear()}/{pad(value.getMonth()+1)}/{pad(value.getDate())}
        </span>
        <span style={{ color: T.textSec, fontSize: 11 }}>{DAYS_AR[value.getDay()]}</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", right: 0, left: 0,
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 6, boxShadow: `0 8px 24px ${T.shadow}`, zIndex: 50,
          padding: "10px 8px 8px", direction: "ltr",
        }}>
          {/* Month / year nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <NavBtn onClick={prevMonth} ch="‹" />
            <span style={{
              fontSize: 12.5, fontWeight: 700, color: T.text,
              fontFamily: "Cairo, sans-serif", direction: "rtl",
            }}>
              {MONTHS_AR[viewMonth]} {viewYear}
            </span>
            <NavBtn onClick={nextMonth} ch="›" />
          </div>

          {/* Day-of-week headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
            {DAYS_SHORT_AR.map((d, i) => (
              <div key={i} style={{
                textAlign: "center", fontSize: 9, fontWeight: 700,
                color: T.textMuted, fontFamily: "Cairo, sans-serif", padding: "1px 0",
              }}>{d}</div>
            ))}
          </div>

          {/* Date cells */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((day, i) => {
              if (!day) return <div key={i} />;
              const d   = new Date(viewYear, viewMonth, day);
              const sel = same(d, value);
              const isTd = same(d, today);
              return (
                <button key={i}
                  onClick={() => { onChange(d); setOpen(false); }}
                  style={{
                    height: 28, border: isTd && !sel ? `1px solid ${accent}66` : "none",
                    borderRadius: 4,
                    background: sel ? accent : "transparent",
                    color: sel ? "#fff" : (isTd ? accent : T.text),
                    fontWeight: sel || isTd ? 700 : 500,
                    fontFamily: "JetBrains Mono, monospace", fontSize: 11,
                    cursor: "pointer", transition: "background .12s",
                  }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.surface3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                >{day}</button>
              );
            })}
          </div>

          {/* Today shortcut */}
          <div style={{ marginTop: 8, borderTop: `1px solid ${T.borderS}`, paddingTop: 8 }}>
            <button
              onClick={() => {
                onChange(new Date(today));
                setViewYear(today.getFullYear());
                setViewMonth(today.getMonth());
                setOpen(false);
              }}
              style={{
                width: "100%", height: 28, border: `1px solid ${T.border}`,
                borderRadius: 5, background: "transparent",
                color: accent, fontFamily: "Cairo, sans-serif",
                fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                transition: "background .12s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = accent + "18"; e.currentTarget.style.borderColor = accent; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = T.border; }}
            >اليوم</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Toggle switch ────────────────────────────────────────────────────────────
function ToggleSwitch({ T, accent, checked, onChange, label, sub }) {
  return (
    <label style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      direction: "rtl", padding: "6px 0", cursor: "pointer",
    }}>
      <div>
        <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{label}</div>
        {sub && <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2 }}>{sub}</div>}
      </div>
      <span
        onClick={() => onChange(!checked)}
        style={{
          width: 32, height: 18, borderRadius: 10,
          background: checked ? accent : T.surface3,
          position: "relative", transition: "background .15s",
          flexShrink: 0, border: `1px solid ${checked ? accent : T.border}`,
        }}
      >
        <span style={{
          width: 12, height: 12, borderRadius: 6, background: "#fff",
          position: "absolute", top: 2, left: checked ? 16 : 2,
          transition: "left .18s cubic-bezier(.4,0,.2,1)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }} />
      </span>
    </label>
  );
}

// ─── Divider ──────────────────────────────────────────────────────────────────
function Div({ T }) {
  return <div style={{ height: 1, background: T.borderS, margin: "6px 0" }} />;
}

// ─── Data transform: ApexCharts series → segments ────────────────────────────
function toTrains(dataset, colors, dark) {
  return (dataset || []).map((series, i) => {
    const segments = [];
    const data = series.data || [];
    for (let k = 0; k + 1 < data.length; k += 2) {
      const entry = data[k][0];
      const exit  = data[k + 1][0];
      const idx   = Number(data[k][1]) - 1; // myIndex is 1-based, convert to 0-based
      if (idx >= 0) segments.push([entry, exit, idx]);
    }
    return {
      name:      series.name,
      color:     ensureReadable((colors && colors[i]) || hashColor(series.name, dark), dark),
      kind:      "freight",
      direction: "down",
      segments,
    };
  }).filter(tr => tr.segments.length > 0);
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ T, values, lists, functions, localState, setLocal, accents }) {
  const { showNowLine, showDwell, autoRefresh, refreshInterval } = localState;

  return (
    <aside style={{
      width: 308, minWidth: 308,
      background: T.surface,
      borderInlineStart: `1px solid ${T.border}`,
      display: "flex", flexDirection: "column",
      overflow: "hidden", flexShrink: 0,
    }}>
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", flex: 1, minHeight: 0 }}>

        {/* Date & Unit section */}
        <Section T={T} label="التاريخ والوحدة" icon={I.cal} accent={accents.date}>
          <DateField T={T} accent={accents.date}
            value={values.selectedDayDate}
            onChange={functions.onDayDateChange}
          />
          <Select T={T} label="نوع الهدف" accent={accents.date}
            value={values.selectedTargetObject}
            onChange={(v) => functions.onChangeTargetObjects(null, v)}
            options={lists.targetObjects}
            getLabel={o => (o && o.caption) || ""}
          />
          {values.selectedTargetObject && values.selectedTargetObject.name === "unit" ? (
            <Select T={T} label="القطار" accent={accents.date}
              value={values.selectedUnit}
              onChange={(v) => functions.onUnitChange(null, v)}
              options={lists.units || []}
              getLabel={o => (o && o.$$user_name) || ""}
            />
          ) : (
            <Select T={T} label="اسم المجموعة" accent={accents.date}
              value={values.selectedGroup}
              onChange={(v) => functions.onGroupChange(null, v)}
              options={lists.groups || []}
              getLabel={o => (o && o.$$user_name) || ""}
            />
          )}
        </Section>

        {/* Route section */}
        <Section T={T} label="المسار" icon={I.route} accent={accents.route}>
          <Select T={T} label="محطة الانطلاق" accent={accents.route}
            value={values.selectedFromStation}
            onChange={(v) => functions.onFromStationChange(null, v)}
            options={lists.fromList || []}
            getLabel={o => (o && o.caption) || ""}
          />
          <Select T={T} label="محطة الوصول" accent={accents.route}
            value={values.selectedToStation}
            onChange={(v) => functions.onToStationChange(null, v)}
            options={lists.toList || []}
            getLabel={o => (o && o.caption) || ""}
          />
          {/* Route stats row */}
          {values.selectedFromStation && values.selectedToStation && (() => {
            const key  = (values.selectedFromStation.name || "") + "_" + (values.selectedToStation.name || "");
            const sts  = routesList[key] || [];
            const last = sts[sts.length - 1];
            return sts.length > 0 ? (
              <div style={{
                marginTop: 4, padding: "6px 8px", background: T.surface2, borderRadius: 5,
                direction: "rtl", display: "flex", alignItems: "center", gap: 6,
                fontSize: 11, color: T.textSec,
              }}>
                <span style={{ color: accents.route }}>{I.pin}</span>
                <span style={{ fontFamily: "JetBrains Mono, monospace" }}>{sts.length}</span>
                <span>محطات</span>
                {last && (
                  <span style={{ marginInlineStart: "auto", fontFamily: "JetBrains Mono, monospace", color: T.textMuted }}>
                    {last.km} كم
                  </span>
                )}
              </div>
            ) : null;
          })()}
        </Section>

        {/* Display options section */}
        <Section T={T} label="خيارات العرض" icon={<span style={{ fontSize: 11, fontWeight: 700 }}>※</span>} accent={accents.opts}>
          <ToggleSwitch T={T} accent={accents.opts}
            checked={values.customColor}
            onChange={v => functions.onCustomColorChange({ target: { checked: v } })}
            label="ألوان عشوائية" sub="إعادة توليد لون كل قطار" />
          <Div T={T} />
          <ToggleSwitch T={T} accent={accents.opts}
            checked={showNowLine}
            onChange={v => setLocal("showNowLine", v)}
            label="خط اللحظة الحالية" sub="مؤشر بتوقيت بغداد" />
          <Div T={T} />
          <ToggleSwitch T={T} accent={accents.opts}
            checked={showDwell}
            onChange={v => setLocal("showDwell", v)}
            label="تظليل التوقف" sub="إبراز مدة الوقوف بكل محطة" />
          <Div T={T} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", direction: "rtl", padding: "6px 0" }}>
            <div>
              <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>اتجاه المحور</div>
              <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2 }}>
                {values.yDirection === "down" ? "البداية أعلى الشاشة" : "البداية أسفل الشاشة"}
              </div>
            </div>
            <button
              onClick={functions.onDirectionChange}
              style={{
                width: 28, height: 28, borderRadius: 6,
                background: T.surface2, border: `1px solid ${T.border}`,
                color: T.text, cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
              title="عكس الاتجاه"
            >{values.yDirection === "down" ? I.arrowDn : I.arrowUp}</button>
          </div>
          <Div T={T} />
          <ToggleSwitch T={T} accent={accents.opts}
            checked={autoRefresh}
            onChange={v => setLocal("autoRefresh", v)}
            label="التحديث التلقائي" sub="إعادة تنفيذ التقرير دورياً" />
          {autoRefresh && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", direction: "rtl", padding: "6px 0 2px" }}>
              <span style={{ fontSize: 11.5, color: T.textSec, fontWeight: 500 }}>كل</span>
              <div style={{
                display: "inline-flex", alignItems: "center",
                background: T.surface2, border: `1px solid ${T.border}`,
                borderRadius: 5, overflow: "hidden",
              }}>
                <button
                  onClick={() => setLocal("refreshInterval", Math.max(15, refreshInterval - 15))}
                  style={{ width: 24, height: 26, border: "none", background: "transparent", color: T.textSec, cursor: "pointer", fontSize: 14, fontWeight: 700 }}
                >−</button>
                <input
                  type="number" min="15" max="3600" step="15"
                  value={refreshInterval}
                  onChange={e => setLocal("refreshInterval", Math.max(15, Math.min(3600, Number(e.target.value) || 15)))}
                  style={{
                    width: 48, height: 26, border: "none", background: "transparent",
                    textAlign: "center", fontFamily: "JetBrains Mono, monospace",
                    fontSize: 12, color: T.text, fontWeight: 700,
                    borderInline: `1px solid ${T.borderS}`, outline: "none",
                  }}
                />
                <button
                  onClick={() => setLocal("refreshInterval", Math.min(3600, refreshInterval + 15))}
                  style={{ width: 24, height: 26, border: "none", background: "transparent", color: T.textSec, cursor: "pointer", fontSize: 14, fontWeight: 700 }}
                >+</button>
              </div>
              <span style={{ fontSize: 11.5, color: T.textSec, fontWeight: 500 }}>ثانية</span>
            </div>
          )}
        </Section>

      </div>

      {/* Execute button — fixed at bottom */}
      <div style={{ padding: 12, borderTop: `1px solid ${T.border}`, background: T.surface2 }}>
        <button
          onClick={functions.execute}
          style={{
            width: "100%", height: 38,
            background: T.text, color: T.bg,
            border: "none", borderRadius: 6,
            fontFamily: "Cairo, sans-serif", fontSize: 13, fontWeight: 700,
            cursor: "pointer", direction: "rtl",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            boxShadow: `0 1px 0 ${T.border}, 0 4px 12px ${T.shadow}`,
            transition: "transform .08s",
          }}
          onMouseDown={e => { e.currentTarget.style.transform = "scale(.98)"; }}
          onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
        >
          <span style={{ color: T.bg, display: "inline-flex" }}>{I.exec}</span>
          تنفيذ التقرير
        </button>
        <div style={{ marginTop: 8, fontSize: 11, color: T.textMuted, direction: "rtl", textAlign: "center" }}>
          {autoRefresh ? (
            <span>تحديث تلقائي كل&nbsp;
              <span style={{ fontFamily: "JetBrains Mono, monospace", color: T.textSec, fontWeight: 700 }}>
                {refreshInterval}
              </span>&nbsp;ثانية
            </span>
          ) : (
            <span>التحديث التلقائي متوقف</span>
          )}
        </div>
      </div>
    </aside>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ T }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      height: "100%", gap: 14, direction: "rtl",
    }}>
      <div style={{
        width: 72, height: 72, borderRadius: "50%",
        background: T.surface2, border: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="3" width="16" height="16" rx="2"/><path d="M4 11h16M8 15h.01M16 15h.01M8 19l-2 3M16 19l2 3"/>
        </svg>
      </div>
      <span style={{ fontSize: 14, color: T.textSec, fontWeight: 600 }}>لا توجد بيانات لعرضها</span>
      <span style={{ fontSize: 12, color: T.textMuted }}>اختر المسار والتاريخ ثم نفّذ التقرير</span>
    </div>
  );
}

// ─── AppLayout (main export) ──────────────────────────────────────────────────
export default function AppLayout(props) {
  const [open,            setOpen]            = useState(true);
  const [dark,            setDark]            = useState(false);
  const [showNowLine,     setShowNowLine]     = useState(true);
  const [showDwell,       setShowDwell]       = useState(true);
  const [autoRefresh,     setAutoRefresh]     = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(120);
  const [highlight,       setHighlight]       = useState(null);
  const [isFullscreen,    setIsFullscreen]    = useState(false);
  const [isExporting,     setIsExporting]     = useState(false);
  const [compact,         setCompact]         = useState(true);
  const chartCardRef = useRef(null);
  const chartRef     = useRef(null);

  useEffect(() => {
    function onFsChange() { setIsFullscreen(!!document.fullscreenElement); }
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      chartCardRef.current && chartCardRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  const T       = dark ? DARK : LIGHT;
  const accents = { route: T.accent, date: T.primary, opts: T.signal };

  function setLocal(key, val) {
    if (key === "showNowLine")      setShowNowLine(val);
    else if (key === "showDwell")   setShowDwell(val);
    else if (key === "autoRefresh") setAutoRefresh(val);
    else if (key === "refreshInterval") setRefreshInterval(val);
  }

  // Build route object for chart
  const selectedArrName = props.data.selectedArrName || "";
  const stationsData    = routesList[selectedArrName] || [];
  const route = {
    fromCap:  (props.values.selectedFromStation && props.values.selectedFromStation.caption) || "",
    toCap:    (props.values.selectedToStation   && props.values.selectedToStation.caption)   || "",
    stations: stationsData.map(s => ({ name: s.name, km: s.km })),
  };

  // Transform dataset to segments format
  const trains = useMemo(
    () => toTrains(props.data.dataset, props.lists.colors, dark),
    [props.data.dataset, props.lists.colors, dark]
  );

  // Baghdad day boundaries for chart X-axis
  const d          = props.values.selectedDayDate;
  const dayStartMs = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  const nowMs      = Date.now() + BGD_MS;

  const trainCount   = trains.length;
  const hasData      = trainCount > 0 && route.stations.length > 0;
  const lastStation  = route.stations[route.stations.length - 1];

  const localState = { showNowLine, showDwell, autoRefresh, refreshInterval };

  // Build export filename stem: from - to - group/unit - date
  const targetIsUnit = props.values.selectedTargetObject &&
                       props.values.selectedTargetObject.name === "unit";
  const groupLabel   = targetIsUnit
    ? (props.values.selectedUnit  && props.values.selectedUnit.$$user_name)  || ""
    : (props.values.selectedGroup && props.values.selectedGroup.$$user_name) || "";
  const exportStem = [
    route.fromCap,
    route.toCap,
    groupLabel,
    d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"),
  ].filter(Boolean).join(" - ");

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        @keyframes irr-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: .55; transform: scale(.92); }
        }
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { height: 100%; overflow: hidden; }
        body { background: ${T.bg}; font-family: 'Cairo', sans-serif; }
        button { font-family: 'Cairo', sans-serif; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${T.surface3}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${T.textMuted}; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        :fullscreen { background: ${T.bg}; border-radius: 0 !important; }
        :-webkit-full-screen { background: ${T.bg}; border-radius: 0 !important; }
      `}</style>

      <div style={{
        height: "100vh", display: "flex", flexDirection: "column",
        background: T.bg, color: T.text,
        fontFamily: "Cairo, sans-serif", overflow: "hidden",
      }}>

        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <header style={{
          height: 52, flexShrink: 0,
          background: T.surface, borderBottom: `1px solid ${T.border}`,
          display: "grid", gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          padding: "0 14px",
          boxShadow: `0 1px 8px ${T.shadow}`, zIndex: 30,
        }}>
          {/* Left zone: brand + theme toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BrandMark size={30} sage={T.accent} salmon={T.signal} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.text }}>مخطط سير القطارات</span>
              <span style={{ fontSize: 10.5, fontWeight: 600, color: T.textMuted, letterSpacing: ".02em" }}>
                السكك الحديد العراقية · غرفة العمليات
              </span>
            </div>
            <div style={{
              marginInlineStart: 8, padding: "4px 11px",
              background: T.accent + "1f", border: `1px solid ${T.accent}55`,
              borderRadius: 4, fontSize: 11.5, color: T.accent, fontWeight: 700,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: T.accent, animation: "irr-pulse 2s infinite" }} />
              متصل بالنظام
            </div>
            {/* Theme toggle */}
            <button
              onClick={() => setDark(v => !v)}
              style={{
                width: 32, height: 32, borderRadius: 6,
                background: T.surface2, border: `1px solid ${T.border}`,
                color: T.textSec, cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
              title={dark ? "الوضع الفاتح" : "الوضع الداكن"}
            >{dark ? I.sun : I.moon}</button>
          </div>

          {/* Center zone: Baghdad clock */}
          <BaghdadClock T={T} />

          {/* Right zone: hamburger (sidebar is on the right) */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <button
              onClick={() => setOpen(o => !o)}
              style={{
                width: 32, height: 32, borderRadius: 6,
                background: open ? T.surface2 : "transparent",
                border: `1px solid ${open ? T.border : "transparent"}`,
                color: T.textSec, cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
              title="القائمة الجانبية"
            >{I.menu}</button>
          </div>
        </header>

        {/* ── Body (RTL: sidebar on right) ────────────────────────────────── */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", direction: "rtl" }}>

          {open && (
            <Sidebar
              T={T}
              values={props.values}
              lists={props.lists}
              functions={props.functions}
              localState={localState}
              setLocal={setLocal}
              accents={accents}
            />
          )}

          {/* Main chart area */}
          <main style={{
            flex: 1, padding: 14, overflow: "hidden",
            display: "flex", flexDirection: "column", direction: "rtl",
          }}>
            <div ref={chartCardRef} style={{
              flex: 1, background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: 10, boxShadow: `0 2px 12px ${T.shadow}`,
              display: "flex", flexDirection: "column", overflow: "hidden",
            }}>

              {/* Chart header */}
              <div style={{
                padding: "10px 16px", borderBottom: `1px solid ${T.border}`,
                display: isFullscreen ? "grid" : "flex",
                gridTemplateColumns: isFullscreen ? "1fr auto 1fr" : undefined,
                alignItems: "center", justifyContent: isFullscreen ? undefined : "space-between",
                gap: 12, flexWrap: "wrap", background: T.surface, flexShrink: 0,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: T.text }}>مخطط سير القطارات</span>
                  <span style={{ fontSize: 11, color: T.textMuted, letterSpacing: ".02em" }}>مخطط الزمن والمسافة</span>
                </div>
                {isFullscreen && <BaghdadClock T={T} />}
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: isFullscreen ? "flex-end" : undefined }}>
                  {route.fromCap && route.toCap && (
                    <Chip T={T} color={T.accent}>
                      <span style={{ display: "inline-flex" }}>{I.route}</span>
                      {route.fromCap} ← {route.toCap}
                    </Chip>
                  )}
                  <Chip T={T}>
                    <span style={{ display: "inline-flex" }}>{I.cal}</span>
                    {fmtDateAr(props.values.selectedDayDate)}
                  </Chip>
                  {trainCount > 0 && (
                    <Chip T={T} color={T.primary}>
                      <span style={{ display: "inline-flex" }}>{I.train}</span>
                      {trainCount} قطار
                    </Chip>
                  )}
                  {/* Compact toggle */}
                  <button
                    onClick={() => setCompact(c => !c)}
                    title={compact ? "عرض عادي مع التمرير" : "ضغط المحطات لتناسب الشاشة"}
                    style={{
                      height: 30, padding: "0 10px", borderRadius: 6,
                      background: compact ? T.accent + "22" : T.surface2,
                      border: `1px solid ${compact ? T.accent : T.border}`,
                      color: compact ? T.accent : T.textSec,
                      cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 5,
                      fontFamily: "Cairo, sans-serif", fontSize: 11, fontWeight: 700,
                      transition: "all .15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; }}
                    onMouseLeave={e => { if (!compact) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSec; } }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                      {compact
                        ? <><line x1="3" y1="3" x2="21" y2="3"/><line x1="3" y1="21" x2="21" y2="21"/><polyline points="8 8 12 4 16 8"/><polyline points="8 16 12 20 16 16"/><line x1="12" y1="4" x2="12" y2="20"/></>
                        : <><line x1="3" y1="3" x2="21" y2="3"/><line x1="3" y1="21" x2="21" y2="21"/><polyline points="8 12 12 8 16 12"/><polyline points="8 12 12 16 16 12"/></>
                      }
                    </svg>
                    {compact ? "عرض عادي" : "ضغط المحطات"}
                  </button>
                  {/* Export image */}
                  <button
                    onClick={async () => {
                      if (!chartRef.current || isExporting) return;
                      setIsExporting(true);
                      await chartRef.current.exportImage();
                      setIsExporting(false);
                    }}
                    title="تصدير صورة عالية الدقة"
                    disabled={isExporting}
                    style={{
                      width: 30, height: 30, borderRadius: 6,
                      background: isExporting ? T.accent + "22" : T.surface2,
                      border: `1px solid ${isExporting ? T.accent : T.border}`,
                      color: isExporting ? T.accent : T.textSec,
                      cursor: isExporting ? "wait" : "pointer",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      transition: "all .15s",
                    }}
                    onMouseEnter={e => { if (!isExporting) { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; } }}
                    onMouseLeave={e => { if (!isExporting) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSec; } }}
                  >
                    {I.download}
                  </button>
                  {/* Fullscreen toggle */}
                  <button
                    onClick={toggleFullscreen}
                    title={isFullscreen ? "تصغير" : "ملء الشاشة"}
                    style={{
                      width: 30, height: 30, borderRadius: 6,
                      background: isFullscreen ? T.accent + "22" : T.surface2,
                      border: `1px solid ${isFullscreen ? T.accent : T.border}`,
                      color: isFullscreen ? T.accent : T.textSec,
                      cursor: "pointer", marginInlineStart: 4,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      transition: "all .15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; }}
                    onMouseLeave={e => { if (!isFullscreen) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSec; } }}
                  >
                    {isFullscreen ? I.shrink : I.expand}
                  </button>
                </div>
              </div>

              {/* Chart body */}
              <div style={{ flex: 1, overflow: "hidden", background: T.bg }}>
                {hasData ? (
                  <MareyChart
                    ref={chartRef}
                    route={route}
                    trains={trains}
                    exportStem={exportStem}
                    dayStartMs={dayStartMs}
                    yDirection={props.values.yDirection}
                    theme={T}
                    showNowLine={showNowLine}
                    nowMs={nowMs}
                    showDwell={showDwell}
                    highlightTrain={highlight}
                    setHighlightTrain={setHighlight}
                    compact={compact}
                  />
                ) : (
                  <EmptyState T={T} />
                )}
              </div>

              {/* Legend */}
              {hasData && (
                <Legend T={T} trains={trains} highlight={highlight} setHighlight={setHighlight} />
              )}

              {/* Footer status bar */}
              <div style={{
                padding: "6px 14px", borderTop: `1px solid ${T.border}`,
                background: T.surface2,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                fontSize: 11, color: T.textMuted,
              }}>
                <div style={{ display: "flex", gap: 14, direction: "rtl" }}>
                  <span>
                    <span style={{ fontFamily: "JetBrains Mono, monospace", color: T.textSec }}>{trainCount}</span>
                    {" "}قطار
                  </span>
                  <span>
                    <span style={{ fontFamily: "JetBrains Mono, monospace", color: T.textSec }}>{route.stations.length}</span>
                    {" "}محطة
                  </span>
                  {lastStation && (
                    <span>
                      <span style={{ fontFamily: "JetBrains Mono, monospace", color: T.textSec }}>{lastStation.km}</span>
                      {" "}كم
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 14, alignItems: "center", direction: "rtl" }}>
                  <span style={{ color: T.accent, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 3, background: T.accent, animation: "irr-pulse 2s infinite" }} />
                    مباشر
                  </span>
                </div>
              </div>

            </div>
          </main>
        </div>
      </div>
    </>
  );
}
